import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { PreKPracticeSettings } from '../types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../../hooks/usePreKAudio';
import { CountingDots } from './types';
import { NUMERAL_FONT_STYLE } from '../../PreKAssessment/preKConstants';
import {
  PracticeHeader,
  PRACTICE_PHASES,
  PRACTICE_TIMER_CONSTANTS,
  factIdToValue,
  PREK_PRACTICE_TRACKS,
  PRACTICE_VISUAL_CONFIG,
} from '../shared';

interface IDoScreenProps {
  settings: PreKPracticeSettings;
  factsToPractice: string[];
  onComplete: () => void;
  onBack: () => void;
}

const TIMING = {
  FLASH_DURATION_MS: 700,      // How long each dot stays highlighted
  PAUSE_BETWEEN_DOTS_MS: 200,  // Brief pause between dots
  SHOW_RESULT_DELAY_MS: 500,   // Delay before showing "I counted X"
};

const IDoScreen: React.FC<IDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [currentPattern, setCurrentPattern] = useState<CountingDots | null>(null);

  // Counting animation state (all dots visible, only flash changes)
  const [flashingDotIndex, setFlashingDotIndex] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);

  // Sequence control - increment to cancel any running sequence
  const sequenceIdRef = useRef(0);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const { playTTSWithCallback } = usePreKTTS();

  const currentFactId = factsToPractice[currentFactIndex];
  const currentQuantity = currentFactId
    ? factIdToValue(PREK_PRACTICE_TRACKS.COUNTING, currentFactId)
    : undefined;

  // Clear all pending timeouts
  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(t => clearTimeout(t));
    timeoutsRef.current = [];
  }, []);

  // Helper to add a tracked timeout
  const addTimeout = useCallback((callback: () => void, delay: number) => {
    const t = setTimeout(callback, delay);
    timeoutsRef.current.push(t);
    return t;
  }, []);

  // Generate dot pattern for current quantity
  const generateCountingDots = useCallback((quantity: number): CountingDots => {
    const gridPositions = PRACTICE_VISUAL_CONFIG.COUNTING_GRID;

    // Randomly select which positions to use
    const shuffledPositions = [...gridPositions].sort(() => Math.random() - 0.5);
    const selectedPositions = shuffledPositions.slice(0, quantity);

    // Sort in reading order: top-to-bottom, then left-to-right
    selectedPositions.sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;  // Top to bottom first
      return a.x - b.x;                    // Then left to right
    });

    const dots = selectedPositions.map((pos, i) => ({
      x: pos.x,
      y: pos.y,
      id: `dot-${i}`
    }));

    return {
      id: `pattern-${Date.now()}-${Math.random()}`,
      quantity,
      dots
    };
  }, []);

  // Count a single dot: show it, flash it, play audio, then call next
  const countDot = useCallback((
    dotIndex: number,
    quantity: number,
    seqId: number,
    onAllDone: () => void
  ) => {
    // Check if this sequence is still valid
    if (sequenceIdRef.current !== seqId) return;

    if (dotIndex >= quantity) {
      // All dots counted
      onAllDone();
      return;
    }

    // Highlight this dot
    setFlashingDotIndex(dotIndex);

    // Get the audio for this number (1-indexed)
    const numberAudio = QUESTION_SCREEN_AUDIO.countingNumbers[(dotIndex + 1) as keyof typeof QUESTION_SCREEN_AUDIO.countingNumbers];

    const proceedToNext = () => {
      if (sequenceIdRef.current !== seqId) return;

      // Remove highlight after flash duration
      addTimeout(() => {
        if (sequenceIdRef.current !== seqId) return;
        setFlashingDotIndex(null);

        // Brief pause then count next dot
        addTimeout(() => {
          countDot(dotIndex + 1, quantity, seqId, onAllDone);
        }, TIMING.PAUSE_BETWEEN_DOTS_MS);
      }, TIMING.FLASH_DURATION_MS);
    };

    // Play the number audio
    if (settings.speechEnabled && numberAudio) {
      playTTSWithCallback(numberAudio, proceedToNext).catch(proceedToNext);
    } else {
      // No speech - just use timing
      addTimeout(proceedToNext, 500);
    }
  }, [settings.speechEnabled, playTTSWithCallback, addTimeout]);

  // Run the full counting sequence
  const runSequence = useCallback((pattern: CountingDots, seqId: number) => {
    const quantity = pattern.quantity;

    // Reset visual state
    setFlashingDotIndex(null);
    setShowResult(false);

    const startCounting = () => {
      if (sequenceIdRef.current !== seqId) return;

      // Count each dot one by one
      countDot(0, quantity, seqId, () => {
        if (sequenceIdRef.current !== seqId) return;

        // All dots counted - show result
        addTimeout(() => {
          if (sequenceIdRef.current !== seqId) return;
          setShowResult(true);

          // Play "I counted X" (using the countingIdo which ends with this)
          // Or we could use iCounted phrase + number, but let's keep it simple
          // Just show the result and auto-advance

          addTimeout(() => {
            if (sequenceIdRef.current !== seqId) return;

            // Advance to next fact
            if (currentFactIndex < factsToPractice.length - 1) {
              setCurrentFactIndex(prev => prev + 1);
            } else {
              addTimeout(() => {
                if (sequenceIdRef.current === seqId) {
                  onComplete();
                }
              }, PRACTICE_TIMER_CONSTANTS.COUNTING_AUTO_ADVANCE_MS);
            }
          }, PRACTICE_TIMER_CONSTANTS.COUNTING_AUTO_ADVANCE_MS);
        }, TIMING.SHOW_RESULT_DELAY_MS);
      });
    };

    // Play intro "Let's count"
    if (settings.speechEnabled) {
      playTTSWithCallback(QUESTION_SCREEN_AUDIO.countingPhrases.letsCount, startCounting)
        .catch(startCounting);
    } else {
      // No speech - start counting after brief delay
      addTimeout(startCounting, 500);
    }
  }, [settings.speechEnabled, playTTSWithCallback, countDot, addTimeout,
      currentFactIndex, factsToPractice.length, onComplete]);

  // Initialize pattern and start sequence when fact changes
  useEffect(() => {
    if (currentQuantity === undefined) return;

    // Cancel any running sequence
    sequenceIdRef.current += 1;
    const currentSeqId = sequenceIdRef.current;
    clearAllTimeouts();

    // Generate new pattern and start sequence
    const pattern = generateCountingDots(currentQuantity);
    setCurrentPattern(pattern);

    // Small delay to let state settle before starting
    const startTimer = setTimeout(() => {
      runSequence(pattern, currentSeqId);
    }, 100);

    return () => {
      clearTimeout(startTimer);
    };
  }, [currentFactIndex, currentQuantity, generateCountingDots, clearAllTimeouts, runSequence]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      sequenceIdRef.current += 1; // Invalidate any running sequence
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  // Loading state
  if (currentQuantity === undefined || !currentPattern) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.I_DO}
        onBack={onBack}
        progress={factsToPractice.length > 0 ? Math.min((currentFactIndex + 1) / factsToPractice.length, 1) : 0}
      />

      {/* Main Content Area - Side-by-side Layout */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-6xl flex items-center justify-center gap-24">
          {/* Left Side: Dots Container - 4x8 grid */}
          <div className="flex-shrink-0" style={{ width: '200px', height: '400px' }}>
            <div className="relative w-full h-full">
              {currentPattern.dots.map((dot, index) => {
                const isFlashing = flashingDotIndex === index;

                return (
                  <motion.div
                    key={dot.id}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{
                      scale: isFlashing ? 1.3 : 1,
                      opacity: 1,
                    }}
                    transition={{
                      scale: { duration: 0.25, ease: 'easeOut' },
                      opacity: { duration: 0.3, ease: 'easeOut' },
                    }}
                    className="absolute w-6 h-6 lg:w-8 lg:h-8 rounded-full shadow-lg"
                    style={{
                      left: `${dot.x}%`,
                      top: `${dot.y}%`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: isFlashing ? '#facc15' : '#2563eb',
                      boxShadow: isFlashing
                        ? '0 0 16px 6px rgba(250, 204, 21, 0.5), 0 0 32px 12px rgba(250, 204, 21, 0.25)'
                        : '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Right Side: Large Blue Number Card */}
          <div className="w-full max-w-md lg:max-w-lg flex items-center justify-center">
            <motion.div
              key={currentQuantity}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: 1,
                scale: showResult ? [1, 1.1, 1] : 1,
              }}
              transition={{
                duration: showResult ? 0.5 : 0.3,
              }}
              className="w-80 h-80 bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl flex items-center justify-center shadow-2xl border-4 border-blue-700"
              style={NUMERAL_FONT_STYLE}
            >
              <span className="text-9xl font-bold text-white">
                {currentQuantity}
              </span>
            </motion.div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default IDoScreen;
